import { HelpPanel, Icon, Link } from '@amzn/awsui-components-react-v3';
import React from 'react';
import '../../styles/spotcp.css';

export const InfoLink = ({ id, onFollow }) => (
  <Link variant="info" id={id} onFollow={onFollow}>
    Info
  </Link>
);

const externalLinkProps = {
  external: true,
  externalIconAriaLabel: 'Opens in a new tab',
};

// a special case of external link, to be used within a link group, where all of them are external
// and we do not repeat the icon
export const ExternalLinkItem = ({ href, text }) => (
  <Link href={href} ariaLabel={`${text} ${externalLinkProps.externalIconAriaLabel}`} target="_blank">
    {text}
  </Link>
);

export function SpotcpInfo() {
  return (
    <HelpPanel
      header={<h2>FLCP Accuracy</h2>}
      footer={
        <div>
          <h4>What is FLCP </h4>
          <div>
            FLCP is a forecast average of Contribution Profit for an ASIN if it ships today. The forecasts are
            derived from 3 cost groups:
          </div>
          <dl>
            <dt>Product Cost (PCOGS)</dt>
            <dd>PCOGS is derived from the FLCP PCOGS cascade</dd>
            <dt>CCOGS (Contra-COGS)</dt>
            <dd>CCOGS values are taken directly from a service the CCOGS team owns</dd>
            <dt>Secondary components</dt>
            <dd>
              secondary components will come from an average based on one of two calculation methodologies: Edison &
              Fleming.
              <ul>
                <li>
                  <b>Edison: </b>a calculation cost model which provides an estimate, or current predicted cost, when
                  there are no past shipments relatively recently for an ASIN
                </li>
                <li>
                  <b>Fleming: </b> If there is at least one past shipment recently for an ASIN, Fleming model is used
                  and values of which come directly from Historical CP
                </li>
              </ul>
            </dd>
            <dt>
              For more information, check{' '}
              <ExternalLinkItem href="https://w.amazon.com/bin/view/ProfitIntelligence/SpotCP/" text="FLCP WIKI" />
            </dt>
          </dl>
        </div>
      }
    >
      <h3>Why we need FLCP Accuracy?</h3>
      <div>
        This FLCP accuracy tool will provide mechanism to measure the accuracy of these forecasts and allows tooling
        to Finance to dive deep for achieving the accuracy goals. Consequently, improved accuracy will result in more
        optimal profitability decisions across consuming systems
      </div>

      <h3>How accuracy is calculated?</h3>
      <div>
        Current methodology is using both HCP First Answer (FA) and Settled Answer (SA) as benchmark. For each day of FLCP
        ASIN forecast, we collect the following 7 days of actual shipment data for that ASIN from HCP backend. and
        then use FLCP and HCP input to calculate Abs Variance Per Unit.
      </div>
    </HelpPanel>
  );
}

export function SpotcpAsinHistogramInfo() {
  return (
    <HelpPanel header={<h2>Spot CP Accuracy ASIN Histogram</h2>}>
      <div>
        The ASIN Histogram session gives you report Shipment & ASIN % and Components per unit amount in each predefined
        net variance bin.
      </div>
      <div>
        <br/>
        <Icon name='status-info' variant='link'/> You can right click on the any of the histogram to perform ASIN analysis for the bin you are interested.
        <br/>
      </div>
      <dl>
        <dt>Net variances:</dt>
        <dd>The difference between SPOT CP and HCP per unit amount</dd>
        <dt>Net variances of Net CP</dt>
        <dd>The difference between SPOT CP Net CP and HCP Net CP per unit amount</dd>
        <dd>
          The Net CP is calculated by (sum of all <b>revenue related components</b>) - ( sum of all{' '}
          <b>cogs related components</b>). It's similair to what's defined in{' '}
          <ExternalLinkItem
            href="https://w.amazon.com/bin/view/ProfitIntelligence/ContributionProfit/CPinWBRTables/"
            text="this WIKI"
          />{' '}
          for contribution profit, but since SPOT CP doesn't support all HCP components, we call it <b>Net CP</b> here.
        </dd>
        <dd>
          e.g. spot Net CP = sum(spot_giftwrap_revenue + spot_discretionary_coop + spot_liquidation_revenue ... ) -
          sum(spot_product_cost_amt + spot_associate_fees_amt + spot_other_cost + ...)
        </dd>
        <dt>Histogram bin definition:</dt>
        <dd>Bin definition is aligned with Finance Spot cp MBR report</dd>
        <dt>Shipment %</dt>
        <dd>The percentage of shipment count in each histogram bin</dd>
        <dt>ASIN %</dt>
        <dd>The percentage of ASIN count in each histogram bin</dd>
        <dt>Component per unit</dt>
        <dd>The Value of HCP component per unit amount in each histogram bin</dd>
      </dl>
    </HelpPanel>
  );
}

export function SpotcpAsinHistroyFilterInfo() {
  return (
    <HelpPanel header={<h2>Spot CP ASIN w/wo History</h2>}>
      <div>
        The AUX component Source session gives you report based on ASIN with and without history using metrics as below:
      </div>
      <ul>
        <li>
          <b>Source: </b>Indicate ASIN with or without history
        </li>
        <li>
          <b>Accuracy: </b>This is total accuracy calculated using sum of weighted error (e.g. SMAP Error x weight) of
          each component
        </li>
        <li>
          <b>Net Variance / Unit Value: </b>This is Net variance for Net CP. The Net CP is calculated by (sum of all{' '}
          <b>revenue related components</b>) - ( sum of all <b>cogs related components</b>). It's similair to what's
          defined in{' '}
          <ExternalLinkItem
            href="https://w.amazon.com/bin/view/ProfitIntelligence/ContributionProfit/CPinWBRTables/"
            text="this WIKI"
          />{' '}
          for contribution profit, but since SPOT CP doesn't support all HCP components, we call it <b>Net CP</b> here.
        </li>
        <li>
          <b>Weight: </b>The percentage of total count of shipment items
        </li>
      </ul>
    </HelpPanel>
  );
}

export function SpotcpAsinAnalysisInfo() {
  return (
    <HelpPanel header={<h2>ASIN Analysis</h2>}>
      <div>ASIN analysis session gives you detail report of the histogram bin you selected in ASIN Histogram report.
        It first gives you summary metrics including net variance of Net CP, Total accuracy etc. of each ASIN. 
        You can further click on individual ASIN to check accuracy report of each component for the ASIN.
      </div>
      <ul>
        <li>
          <b>ASIN: </b>The ASINs in the selected histogram bin
        </li>
        <li>
          <b>Product line: </b> The GL_PRODUCT_GROUP the ASIN belongs to
        </li>
        <li>
          <b>Net CP Net Var: </b>The Net variance of the Net CP of the ASIN
        </li>
        <li>
          <b>Shipment Item Counts: </b> The count of customer_shipment_item_id of the AISN
        </li>
        
        <li>
          <b>Product GMS / Unit Value: </b> Prodcut GMS per unit value
        </li>
        <li>
          <b>Total Accuracy: </b> The sum of weighted error (e.g. SMAP Error x weight) of each
            component.
        </li>
        <li>
          <b>Component: </b> The name of the HCP component
        </li>
        <li>
          <b>Net Variance / Unit Value: </b>The difference between SPOT CP and HCP per unit amount for product cost
        </li>
        <li>
          <b>Accuracy: </b>This is accuracy for product cost component using formula (1 - SMAPE ERROR). SMAPE is defined
          as average of 100% x |spot - hcp| / max(|spot| , |hcp|)
        </li>
        <li>
          <b>Weight: </b>The weight to calculate Total Accuracy. It's calculated by hcp component amount divided by sum of all component amount in absolute values. 
        </li>
      </ul>
    </HelpPanel>
  )
}

export function SpotcpPCogsSourceFilterInfo() {
  return (
    <HelpPanel header={<h2>Product Cogs Source</h2>}>
      <div>The Product Cost Source session gives you report based on Pcogs Source using metrics as below:</div>
      <ul>
        <li>
          <b>Source: </b>Indicate Product Cost Source
        </li>
        <li>
          <b>Accuracy: </b>This is accuracy for product cost component using formula (1 - SMAPE ERROR). SMAPE is defined
          as average of 100% x |spot - hcp| / max(|spot| , |hcp|)
        </li>
        <li>
          <b>Net Variance / Unit Value: </b>The difference between SPOT CP and HCP per unit amount for product cost
        </li>
        <li>
          <b>Weight: </b>The percentage of total count of shipment items
        </li>
      </ul>
    </HelpPanel>
  );
}

export function SpotcpSummaryInfo() {
  return (
    <HelpPanel header={<h2>Spot CP Accuracy Summary</h2>}>
      <div>
        The Summary session gives you report for each SPOT CP Supported HCP Components with metrics as below:
        <ul>
          <li>
            <b>SPOT CP / Unit Value: </b>The sum of SPOT CP forecast amount divided by total count of shipments items
          </li>
          <li>
            <b>HCP / Unit Value: </b>The sum of Actual HCP amount divided by total count of shipment items
          </li>
          <li>
            <b>Net Variance / Unit Value: </b>The difference between SPOT CP and HCP per unit amount
          </li>
          <li>
            <b>Net Variance as % of PGMS: </b>Net Variance / Product GMS per unit
          </li>
          <li>
            <b>Accuracy %: </b>(1 - SMAPE ERROR). SMAPE is defined as average of 100% x |spot - hcp| / max(|spot| ,
            |hcp|)
          </li>
          <li>
            The <b>Total accuracy</b> is calculated using sum of weighted error (e.g. SMAP Error x weight) of each
            component
          </li>
        </ul>
      </div>
    </HelpPanel>
  );
}



// WEBPACK FOOTER //
// ./src/components/spotcp/info.jsx